import { useState, useEffect, useMemo, useRef } from 'react';
import { collection, query, where, onSnapshot, doc as firestoreDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { uploadDocumentToBunny, uploadImageToBunnyDirect } from '../../utils/bunnyUpload';
import { notifyAdmins } from '../../utils/notificationService';
import { useToast } from '../../context/ToastContext';
import {
  FileEdit, Medal, Loader2, CheckCircle2, AlertCircle, Clock,
  ExternalLink, Download, MessageSquare, ChevronDown, ChevronUp,
  Search, FileText, Link as LinkIcon, Filter, TrendingUp, BookOpen, BarChart3,
  Upload, X, Image as ImageIcon, RefreshCw
} from 'lucide-react';
import AssignmentSubmission from '../../components/dashboard/AssignmentSubmission';

// ── Circular Progress Ring ──────────────────────────────────
function GradeRing({ percent, size = 72, stroke = 6, color }) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;
  const ringColor = color || (percent >= 90 ? '#10b981' : percent >= 70 ? '#3b82f6' : percent >= 50 ? '#f59e0b' : '#ef4444');

  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
        stroke="currentColor" className="text-slate-100 dark:text-white/5" strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
        stroke={ringColor} strokeWidth={stroke} strokeLinecap="round"
        strokeDasharray={circumference} strokeDashoffset={offset}
        style={{ transition: 'stroke-dashoffset 1s ease-out' }} />
    </svg>
  );
}

export default function StudentAssignments() {
  const { currentUser } = useAuth();
  const { addToast } = useToast();
  const [submissions, setSubmissions] = useState([]);
  const [courses, setCourses] = useState({});
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  // ── Curriculum & Unified State ──
  const [enrolledCourseIds, setEnrolledCourseIds] = useState([]);
  const [courseAssignmentsMap, setCourseAssignmentsMap] = useState({});
  const [allPossibleAssignments, setAllPossibleAssignments] = useState([]);
  const [submissionModalData, setSubmissionModalData] = useState(null);

  // ── Resubmission state ──
  const [resubmitId, setResubmitId] = useState(null); // which submission is being resubmitted
  const [resubTab, setResubTab] = useState('images'); // images, pdf, drive
  const [resubImages, setResubImages] = useState([]);
  const [resubPdf, setResubPdf] = useState(null);
  const [resubDriveLink, setResubDriveLink] = useState('');
  const [resubNote, setResubNote] = useState('');
  const [resubLoading, setResubLoading] = useState(false);
  const [resubProgress, setResubProgress] = useState(0);
  const imageInputRef = useRef(null);
  const pdfInputRef = useRef(null);

  const resetResubForm = () => {
    setResubmitId(null);
    setResubTab('images');
    setResubImages([]);
    setResubPdf(null);
    setResubDriveLink('');
    setResubNote('');
    setResubProgress(0);
  };

  const handleResubImageChange = (e) => {
    const files = Array.from(e.target.files);
    let valid = [];
    files.forEach(f => {
      if (f.size > 2 * 1024 * 1024) addToast(`حجم ${f.name} يتجاوز 2MB`, 'error');
      else if (!f.type.startsWith('image/')) addToast(`${f.name} ليس صورة صالحة`, 'error');
      else valid.push({ file: f, preview: URL.createObjectURL(f) });
    });
    setResubImages(prev => [...prev, ...valid].slice(0, 3));
  };

  const handleResubPdfChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { addToast('حجم PDF يتجاوز 10MB', 'error'); return; }
    if (file.type !== 'application/pdf') { addToast('يجب أن يكون الملف PDF', 'error'); return; }
    setResubPdf(file);
  };

  const handleResubmit = async (sub) => {
    if (resubTab === 'images' && resubImages.length === 0) return addToast('ارفع صورة واحدة على الأقل', 'warning');
    if (resubTab === 'pdf' && !resubPdf) return addToast('اختر ملف PDF', 'warning');
    if (resubTab === 'drive' && !resubDriveLink.trim()) return addToast('أدخل رابط Drive', 'warning');

    setResubLoading(true);
    setResubProgress(10);
    try {
      const uploadedFiles = [];
      if (resubTab === 'images') {
        let cnt = 0;
        for (const imgObj of resubImages) {
          const url = await uploadImageToBunnyDirect(imgObj.file, 'Assintmint');
          uploadedFiles.push({ name: imgObj.file.name, url });
          cnt++;
          setResubProgress(10 + Math.round((cnt / resubImages.length) * 50));
        }
      } else if (resubTab === 'pdf') {
        const url = await uploadDocumentToBunny(resubPdf, (pct) => setResubProgress(10 + Math.round(pct * 0.5)), 'Assintmint');
        uploadedFiles.push({ name: resubPdf.name, url });
      }

      setResubProgress(70);

      // Update existing submission
      await updateDoc(firestoreDoc(db, 'submissions', sub.id), {
        files: uploadedFiles.length > 0 ? uploadedFiles : (sub.files || []),
        driveLink: resubTab === 'drive' ? resubDriveLink.trim() : (sub.driveLink || null),
        studentNote: resubNote.trim() || null,
        status: 'pending',
        grade: null,
        adminFeedback: null,
        resubmittedAt: serverTimestamp(),
      });

      setResubProgress(85);

      // Notify admins
      await notifyAdmins(
        'إعادة تسليم تكليف',
        `قام الطالب ${currentUser.displayName || ''} بإعادة تسليم: ${sub.assignmentName}`,
        'info',
        '/admin/assignments'
      );

      setResubProgress(100);
      addToast('تم إعادة تسليم التكليف بنجاح!', 'success');
      resetResubForm();
    } catch (err) {
      console.error('[Resubmit] Error:', err);
      addToast('حدث خطأ أثناء إعادة الرفع: ' + err.message, 'error');
    } finally {
      setResubLoading(false);
    }
  };

  // 1. Fetch Enrolled Course IDs and Listen to Each Course Document
  useEffect(() => {
    if (!currentUser) return;

    const enrQ = query(collection(db, 'enrollments'), where('userId', '==', currentUser.uid));

    // This listener handles changes in enrollment
    const unsubEnroll = onSnapshot(enrQ, (snap) => {
      const ids = snap.docs.map(d => d.data().courseId);
      setEnrolledCourseIds(ids);
    });

    return unsubEnroll;
  }, [currentUser]);

  // Track listeners to clean up
  const courseListeners = useRef({});

  useEffect(() => {
    if (enrolledCourseIds.length === 0) {
      setCourses({});
      setAllPossibleAssignments([]);
      return;
    }

    // Clean up listeners for courses no longer enrolled
    Object.keys(courseListeners.current).forEach(cid => {
      if (!enrolledCourseIds.includes(cid)) {
        courseListeners.current[cid]();
        delete courseListeners.current[cid];
      }
    });

    // Add listeners for new courses
    enrolledCourseIds.forEach(cid => {
      if (!courseListeners.current[cid]) {
        courseListeners.current[cid] = onSnapshot(firestoreDoc(db, 'courses', cid), (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            setCourses(prev => ({ ...prev, [cid]: data.title || 'دورة' }));

            // Extract assignments
            const courseAs = [];
            if (data.curriculum) {
              data.curriculum.forEach((unit, ui) => {
                if (unit.lessons) {
                  unit.lessons.forEach((lesson, li) => {
                    if (lesson.type === 'assignment' || lesson.type === 'final_project') {
                      courseAs.push({
                        id: `u${ui}-l${li}`,
                        courseId: cid,
                        courseTitle: data.title,
                        unitTitle: unit.title,
                        ...lesson,
                        uniqueKey: `${cid}-u${ui}-l${li}`
                      });
                    }
                  });
                }
              });
            }
            // Update grouped state
            setCourseAssignmentsMap(prev => ({ ...prev, [cid]: courseAs }));
          }
        });
      }
    });

    return () => {
      // Logic for cleanup of all listeners if component unmounts is handled by the useEffect return in a generic way
      // But we need to be careful with refs. 
    };
  }, [enrolledCourseIds]);

  // Clear all listeners on unmount
  useEffect(() => {
    return () => {
      Object.values(courseListeners.current).forEach(unsub => unsub());
    };
  }, []);

  // Update allPossibleAssignments whenever the map changes
  useEffect(() => {
    const all = Object.values(courseAssignmentsMap).flat();
    setAllPossibleAssignments(all);
  }, [courseAssignmentsMap]);

  // 2. Fetch Student's Submissions & Merge with Curricula
  useEffect(() => {
    if (!currentUser) { setLoading(false); return; }

    const q = query(
      collection(db, 'submissions'),
      where('studentId', '==', currentUser.uid)
    );

    const unsub = onSnapshot(q, (snap) => {
      try {
        const subs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setSubmissions(subs);
        setFetchError(null);

        // Auto-expand the first "needs_revision" item
        const revisionItem = subs.find(s => s.status === 'needs_revision');
        if (revisionItem) setExpandedId(prev => prev || revisionItem.id);
      } catch (err) {
        console.error('[StudentAssignments] Error processing submissions:', err);
        setFetchError(err.message);
      } finally {
        setLoading(false);
      }
    }, (err) => {
      console.error('[StudentAssignments] Firestore listener error:', err);
      setFetchError(err.message);
      setLoading(false);
    });

    return unsub;
  }, [currentUser]);

  // 3. Merge curricula items with submissions to create final list
  const unifiedAssignments = useMemo(() => {
    return allPossibleAssignments.map(item => {
      const submission = submissions.find(s =>
        s.courseId === item.courseId &&
        s.assignmentId === item.id
      );

      if (submission) {
        return { ...item, ...submission, isSubmitted: true, uniqueId: submission.id };
      } else {
        return { ...item, status: 'not_submitted', isSubmitted: false, uniqueId: item.uniqueKey };
      }
    }).sort((a, b) => {
      // Sort by status priority: needs_revision > not_submitted > pending > graded
      const priority = { needs_revision: 0, not_submitted: 1, pending: 2, graded: 3 };
      return (priority[a.status] ?? 4) - (priority[b.status] ?? 4);
    });
  }, [allPossibleAssignments, submissions]);

  // ── Course-level grade aggregation ──
  const courseGrades = useMemo(() => {
    const map = {};
    submissions.forEach(sub => {
      const cid = sub.courseId;
      if (!cid) return;
      if (!map[cid]) map[cid] = { totalScore: 0, totalMax: 0, gradedCount: 0, totalCount: 0, courseName: courses[cid] || 'دورة' };
      map[cid].totalCount++;
      if (sub.status === 'graded' && sub.grade != null) {
        map[cid].gradedCount++;
        map[cid].totalScore += Number(sub.grade);
        map[cid].totalMax += Number(sub.maxScore || 100);
      }
    });
    return map;
  }, [submissions, courses]);

  const statusConfig = {
    not_submitted: {
      label: 'غير ملحق',
      color: 'bg-slate-100 text-slate-700 dark:bg-white/5 dark:text-zinc-400',
      bg: 'bg-slate-500/10',
      borderColor: 'border-slate-200 dark:border-white/10',
      icon: AlertCircle,
      iconColor: 'text-slate-400',
    },
    pending: {
      label: 'تم التسليم',
      color: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400',
      bg: 'bg-amber-500/10',
      borderColor: 'border-amber-200 dark:border-amber-500/20',
      icon: Clock,
      iconColor: 'text-amber-500',
    },
    graded: {
      label: 'تم التقييم',
      color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400',
      bg: 'bg-emerald-500/10',
      borderColor: 'border-emerald-200 dark:border-emerald-500/20',
      icon: CheckCircle2,
      iconColor: 'text-emerald-500',
    },
    needs_revision: {
      label: 'مطلوب تعديل',
      color: 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-400',
      bg: 'bg-rose-500/10',
      borderColor: 'border-rose-200 dark:border-rose-500/20',
      icon: AlertCircle,
      iconColor: 'text-rose-500',
    },
  };

  const filtered = unifiedAssignments.filter(sub => {
    if (activeTab !== 'all' && sub.status !== activeTab) return false;
    const q = searchTerm.toLowerCase();
    return (sub.assignmentName?.toLowerCase().includes(q) || sub.title?.toLowerCase().includes(q) || '');
  });

  const counts = {
    all: unifiedAssignments.length,
    not_submitted: unifiedAssignments.filter(s => s.status === 'not_submitted').length,
    pending: unifiedAssignments.filter(s => s.status === 'pending').length,
    graded: unifiedAssignments.filter(s => s.status === 'graded').length,
    needs_revision: unifiedAssignments.filter(s => s.status === 'needs_revision').length,
  };

  const tabs = [
    { key: 'all', label: 'الكل', icon: Filter, count: counts.all },
    { key: 'not_submitted', label: 'غير مسلّم', icon: AlertCircle, count: counts.not_submitted },
    { key: 'pending', label: 'قيد المراجعة', icon: Clock, count: counts.pending },
    { key: 'graded', label: 'تم التقييم', icon: CheckCircle2, count: counts.graded },
    { key: 'needs_revision', label: 'مطلوب تعديل', icon: AlertCircle, count: counts.needs_revision },
  ];

  const toggleExpand = (id) => {
    setExpandedId(prev => prev === id ? null : id);
  };

  const getGradeLabel = (percent) => {
    if (percent >= 90) return { text: 'ممتاز', cls: 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400' };
    if (percent >= 80) return { text: 'جيد جداً', cls: 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400' };
    if (percent >= 70) return { text: 'جيد', cls: 'bg-sky-100 dark:bg-sky-500/20 text-sky-700 dark:text-sky-400' };
    if (percent >= 50) return { text: 'مقبول', cls: 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400' };
    return { text: 'ضعيف', cls: 'bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-400' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-20">
        <Loader2 className="w-10 h-10 animate-spin text-primary-500" />
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="flex flex-col items-center justify-center p-20 text-center space-y-4">
        <div className="w-16 h-16 bg-rose-50 dark:bg-rose-500/10 rounded-2xl flex items-center justify-center">
          <AlertCircle className="w-8 h-8 text-rose-500" />
        </div>
        <p className="font-bold text-slate-700 dark:text-zinc-300">حدث خطأ في تحميل التكاليف</p>
        <p className="text-sm text-slate-400 dark:text-zinc-500 max-w-md" dir="ltr">{fetchError}</p>
        <button onClick={() => window.location.reload()} className="px-6 py-2 bg-primary-600 text-white rounded-xl font-bold hover:bg-primary-500 transition-colors">
          إعادة المحاولة
        </button>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white mb-2 flex items-center gap-3">
          <div className="p-2.5 bg-primary-50 dark:bg-primary-500/10 rounded-xl">
            <FileEdit className="w-7 h-7 text-primary-600 dark:text-primary-400" />
          </div>
          التكاليف والمشاريع
        </h1>
        <p className="text-slate-500 dark:text-zinc-400 font-medium">
          تتبّع تكاليفك ومشاريعك المسلّمة، وراجع ملاحظات المدربين والتقييمات.
        </p>
      </div>

      {/* ── Revision Alert Banner ── */}
      {counts.needs_revision > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-l from-rose-500/10 to-transparent border border-rose-300 dark:border-rose-500/30 rounded-2xl p-5 flex items-center gap-4 relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-40 h-40 bg-rose-500/10 rounded-full blur-[60px] pointer-events-none" />
          <div className="w-12 h-12 bg-rose-100 dark:bg-rose-500/20 rounded-xl flex items-center justify-center shrink-0 border border-rose-200 dark:border-rose-500/30">
            <AlertCircle className="w-6 h-6 text-rose-500" />
          </div>
          <div className="flex-1 relative z-10">
            <p className="font-black text-rose-700 dark:text-rose-400 text-sm">
              لديك {counts.needs_revision} تكليف بحاجة لتعديل!
            </p>
            <p className="text-rose-600/80 dark:text-rose-300/60 text-xs font-medium mt-0.5">
              راجع ملاحظات المدرب وأعد تسليم التكليف من صفحة الدورة.
            </p>
          </div>
          <button
            onClick={() => setActiveTab('needs_revision')}
            className="px-5 py-2 bg-rose-500 hover:bg-rose-600 text-white font-bold rounded-xl text-sm transition-all shrink-0 relative z-10"
          >
            عرض التفاصيل
          </button>
        </motion.div>
      )}

      {/* ── Course Grade Summary Cards ── */}
      {Object.keys(courseGrades).length > 0 && (
        <div>
          <h2 className="text-lg font-black text-slate-900 dark:text-white mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary-500" />
            الدرجة الكلية لكل دورة
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(courseGrades).map(([cid, data]) => {
              const percent = data.totalMax > 0 ? Math.round((data.totalScore / data.totalMax) * 100) : 0;
              const label = data.gradedCount > 0 ? getGradeLabel(percent) : null;
              return (
                <div key={cid} className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-2xl p-5 flex items-center gap-5 relative overflow-hidden group hover:shadow-lg transition-all">
                  <div className="absolute top-0 left-0 w-32 h-32 bg-primary-500/5 rounded-full blur-2xl -translate-x-10 -translate-y-10 group-hover:scale-150 transition-transform duration-700 pointer-events-none" />

                  {/* Grade Ring */}
                  <div className="relative shrink-0">
                    <GradeRing percent={data.gradedCount > 0 ? percent : 0} size={68} stroke={5} />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-lg font-black text-slate-900 dark:text-white">
                        {data.gradedCount > 0 ? `${percent}%` : '—'}
                      </span>
                    </div>
                  </div>

                  <div className="flex-1 min-w-0 relative z-10">
                    <div className="flex items-center gap-2 mb-1">
                      <BookOpen className="w-4 h-4 text-primary-500 shrink-0" />
                      <p className="font-black text-slate-900 dark:text-white truncate text-sm">{data.courseName}</p>
                    </div>
                    <p className="text-xs font-medium text-slate-400 dark:text-zinc-500">
                      {data.totalScore}/{data.totalMax} • {data.gradedCount} من {data.totalCount} تم تقييمها
                    </p>
                    {label && (
                      <span className={`inline-block mt-2 px-2.5 py-0.5 rounded-full text-[10px] font-black ${label.cls}`}>
                        {label.text}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Stats/Filter Tabs ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {tabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`relative p-4 rounded-2xl border transition-all duration-300 text-right group overflow-hidden ${isActive
                ? 'bg-white dark:bg-zinc-900 border-primary-300 dark:border-primary-500/30 shadow-lg shadow-primary-500/10 ring-2 ring-primary-500/20'
                : 'bg-white/60 dark:bg-zinc-900/40 border-slate-200 dark:border-white/10 hover:border-slate-300 dark:hover:border-white/20 hover:shadow-md'
                }`}
            >
              {isActive && (
                <div className="absolute top-0 right-0 w-24 h-24 bg-primary-500/10 rounded-full blur-2xl -translate-y-6 translate-x-6 pointer-events-none" />
              )}
              <div className="relative z-10 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-colors ${isActive ? 'bg-primary-50 dark:bg-primary-500/10' : 'bg-slate-50 dark:bg-white/5'
                  }`}>
                  <Icon className={`w-5 h-5 ${isActive ? 'text-primary-600 dark:text-primary-400' : 'text-slate-400 dark:text-zinc-500'}`} />
                </div>
                <div>
                  <p className={`text-2xl font-black ${isActive ? 'text-primary-600 dark:text-primary-400' : 'text-slate-900 dark:text-white'}`}>
                    {tab.count}
                  </p>
                  <p className="text-xs font-bold text-slate-500 dark:text-zinc-400">{tab.label}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative w-full md:max-w-sm">
        <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
        <input
          type="text"
          placeholder="البحث باسم التكليف..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="w-full pl-4 pr-11 py-3 bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-slate-900 dark:text-white font-bold"
        />
      </div>

      {/* ── Assignments List ── */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-slate-500 bg-white/60 dark:bg-zinc-900/40 backdrop-blur-3xl rounded-3xl border border-slate-200/50 dark:border-white/10">
          <div className="w-20 h-20 bg-slate-50 dark:bg-white/5 rounded-2xl flex items-center justify-center mb-4">
            <FileEdit className="w-10 h-10 text-slate-300 dark:text-zinc-700" />
          </div>
          <p className="text-xl font-bold text-slate-400 dark:text-zinc-500 mb-1">لا توجد تكاليف</p>
          <p className="text-sm text-slate-400 dark:text-zinc-600">لم يتم تسليم أي تكاليف حتى الآن في هذا القسم.</p>
        </div>
      ) : (
        <div className="space-y-4">
          <AnimatePresence mode="popLayout">
            {filtered.map((sub, idx) => {
              const config = statusConfig[sub.status] || statusConfig.pending;
              const StatusIcon = config.icon;
              const isExpanded = expandedId === sub.id;
              const isProject = sub.assignmentType === 'final_project';
              const gradePercent = sub.grade != null && sub.maxScore ? Math.round((sub.grade / sub.maxScore) * 100) : null;

              return (
                <motion.div
                  key={sub.uniqueId}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ delay: idx * 0.04 }}
                  className={`bg-white dark:bg-zinc-900 border ${config.borderColor} rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300`}
                >
                  {/* Main Row - Premium Redesign */}
                  <div
                    onClick={() => toggleExpand(sub.uniqueId)}
                    className="w-full flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 p-5 sm:p-6 text-right group cursor-pointer hover:bg-slate-50/50 dark:hover:bg-white/[0.02] transition-all relative overflow-hidden"
                  >
                    {/* Hover Glow Effect */}


                    {/* Left side Accent (Status Color) */}


                    {/* 1. Assignment Title (Top on Mobile) */}
                    <div className="w-full sm:order-2 sm:flex-1 min-w-0">
                      <h3 className="font-black text-lg sm:text-xl text-slate-900 dark:text-white truncate tracking-tight group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors mb-2 sm:mb-1.5">
                        {sub.assignmentName || sub.title || 'تكليف بدون عنوان'}
                      </h3>

                      <div className="hidden sm:flex flex-wrap items-center gap-3">
                        {/* Course Badge (Desktop) */}
                        {courses[sub.courseId] && (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 text-[11px] font-black rounded-lg border border-primary-100 dark:border-primary-500/20">
                            <BookOpen className="w-3 h-3" />
                            {courses[sub.courseId]}
                          </span>
                        )}

                        {/* Date/Status Pill (Desktop) */}
                        <div className="flex items-center gap-1.5 text-[11px] font-bold text-slate-400 dark:text-zinc-500">
                          {sub.isSubmitted ? (
                            <>
                              <Clock className="w-3.5 h-3.5" />
                              <span dir="ltr">{sub.submittedAt?.toDate ? sub.submittedAt.toDate().toLocaleDateString('en-GB') : '—'}</span>
                            </>
                          ) : (
                            <span className="text-rose-500 flex items-center gap-1.5 bg-rose-50 dark:bg-rose-500/10 px-2.5 py-1 rounded-lg border border-rose-100 dark:border-rose-500/20">
                              <AlertCircle className="w-3.5 h-3.5" />
                              غير مسلّم بعد
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* 2. Icon & Meta Data (Middle on Mobile) */}
                    <div className="flex items-center gap-4 sm:order-1 shrink-0">
                      <div className="relative">
                        <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center border transition-all duration-500 group-hover:scale-105 group-hover:rotate-3 shadow-lg ${isProject
                          ? 'bg-gradient-to-br from-rose-50 to-rose-100 dark:from-rose-500/20 dark:to-rose-500/5 border-rose-200/50 dark:border-rose-500/20 '
                          : 'bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-500/20 dark:to-emerald-500/5 border-emerald-200/50 dark:border-emerald-500/20'
                          }`}>
                          {isProject
                            ? <Medal className="w-7 h-7 sm:w-8 sm:h-8 text-rose-500 drop-shadow-sm" />
                            : <FileEdit className="w-7 h-7 sm:w-8 sm:h-8 text-emerald-500 drop-shadow-sm" />
                          }
                        </div>
                        {/* Status Dot */}
                        <div className={`absolute -top-1 -right-1 w-5 h-5 rounded-full border-2 border-white dark:border-zinc-900 flex items-center justify-center shadow-sm ${config.bg.replace('bg-', 'bg-').replace('/10', '')}`}>
                          <StatusIcon className="w-3 h-3 text-white" />
                        </div>
                      </div>

                      <div className="flex-1 sm:hidden">
                        {/* Course Data & Status (Mobile View) */}
                        <div className="flex flex-col gap-1">
                          <span className="text-md font-black text-slate-900 dark:text-white truncate max-w-[150px]">
                            {courses[sub.courseId]}
                          </span>
                          <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-400 dark:text-zinc-500">
                            {sub.isSubmitted ? (
                              <span dir="ltr">{sub.submittedAt?.toDate ? sub.submittedAt.toDate().toLocaleDateString('en-GB') : '—'}</span>
                            ) : (
                              <span className="text-red-500 font-medium text-sm">غير مسلّم بعد</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Score Circle (Mobile Only) */}
                      {sub.status === 'graded' && gradePercent != null && (
                        <div className="relative sm:hidden">
                          <GradeRing percent={gradePercent} size={44} stroke={4} />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-[10px] font-black text-slate-900 dark:text-white">{gradePercent}%</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* 3. Result & Action Button (Bottom on Mobile) */}
                    <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-6 sm:order-3 pt-2 sm:pt-0 border-t border-slate-100 sm:border-0 dark:border-white/5">
                      {/* Score Circle (Desktop Only) */}
                      {sub.status === 'graded' && gradePercent != null && (
                        <div className="hidden sm:block relative group/score">
                          <GradeRing percent={gradePercent} size={48} stroke={5} />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-[12px] font-black text-slate-900 dark:text-white">{gradePercent}%</span>
                          </div>
                        </div>
                      )}

                      {/* Prominent Action Button */}
                      {(sub.status === 'not_submitted' || sub.status === 'needs_revision') && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (sub.status === 'not_submitted') {
                              setSubmissionModalData({
                                ...sub,
                                id: sub.id,
                                sessionData: { ...sub, type: isProject ? 'final_project' : 'assignment' }
                              });
                            } else {
                              toggleExpand(sub.uniqueId);
                            }
                          }}
                          className={`relative overflow-hidden group/btn px-6 py-2.5 sm:px-4 rounded-2xl font-black text-sm flex-1 sm:flex-none flex items-center justify-center gap-2 transition-all shadow-lg active:scale-95 ${sub.status === 'not_submitted'
                            ? 'bg-primary-600 text-white shadow-primary-600/20 hover:bg-primary-500'
                            : 'bg-rose-500 text-white shadow-rose-500/20 hover:bg-rose-600'
                            }`}
                        >
                          <div className="absolute inset-0 bg-white/20 translate-y-full group-hover/btn:translate-y-0 transition-transform duration-300"></div>
                          <Upload className="w-4.5 h-4.5 relative z-10" />
                          <span className="relative z-10">{sub.status === 'not_submitted' ? 'تسليم التكليف' : 'تعديل الملفات'}</span>
                        </button>
                      )}

                      {/* Styled Expand Arrow */}
                      <div className={`p-2 rounded-xl transition-all duration-300 ${isExpanded ? 'bg-slate-100 dark:bg-white/10 text-primary-500 rotate-180' : 'text-slate-300 dark:text-zinc-600 group-hover:text-slate-500'}`}>
                        <ChevronDown className="w-6 h-6" />
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-5 pb-5 space-y-4 border-t border-slate-100 dark:border-white/5 pt-4">

                          {/* ── Submission Required (Not Submitted) ── */}
                          {sub.status === 'not_submitted' && (
                            <div className="bg-slate-50 dark:bg-white/5 rounded-2xl p-6 text-center space-y-4 border border-dashed border-slate-200 dark:border-white/10">
                              <div className="w-16 h-16 bg-primary-500/10 rounded-full flex items-center justify-center mx-auto">
                                <Upload className="w-8 h-8 text-primary-500" />
                              </div>
                              <div>
                                <p className="font-black text-slate-900 dark:text-white mb-1">حان الوقت لرفع إبداعك!</p>
                                <p className="text-sm text-slate-500 dark:text-zinc-400">هذا التكليف لم يتم تسليمه بعد. يمكنك رفعه هنا مباشرة.</p>
                              </div>
                              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                                <button
                                  onClick={() => setSubmissionModalData({ ...sub, sessionData: { ...sub, id: sub.id, type: isProject ? 'final_project' : 'assignment' } })}
                                  className="px-8 py-3 bg-primary-600 hover:bg-primary-500 text-white font-black rounded-xl transition-all shadow-lg shadow-primary-600/20 flex items-center justify-center gap-2"
                                >
                                  <Upload className="w-5 h-5" />
                                  رفع التكليف الآن
                                </button>
                                <a
                                  href={`/watch/${sub.courseId}`}
                                  className="px-8 py-3 bg-white dark:bg-zinc-800 text-slate-700 dark:text-white font-bold rounded-xl border border-slate-200 dark:border-white/10 hover:bg-slate-50 transition-all flex items-center justify-center gap-2"
                                >
                                  مشاهدة الدرس <ExternalLink className="w-5 h-5" />
                                </a>
                              </div>
                            </div>
                          )}

                          {/* ── Revision Alert + Resubmit Form ── */}
                          {sub.status === 'needs_revision' && (
                            <div className="space-y-4">
                              {/* Admin Feedback */}
                              <div className="bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/20 rounded-xl p-4">
                                <div className="flex items-start gap-3">
                                  <AlertCircle className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />
                                  <div>
                                    <p className="font-black text-rose-700 dark:text-rose-400 text-sm mb-1">⚠️ مطلوب تعديل على هذا التكليف</p>
                                    {sub.adminFeedback ? (
                                      <p className="text-rose-600 dark:text-rose-300 font-medium text-sm leading-relaxed">{sub.adminFeedback}</p>
                                    ) : (
                                      <p className="text-rose-500/60 dark:text-rose-400/60 text-sm font-medium">لم تُرفق ملاحظات تفصيلية. تواصل مع الإدارة للتوضيح.</p>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Resubmit Toggle */}
                              {resubmitId !== sub.id ? (
                                <button
                                  onClick={() => { resetResubForm(); setResubmitId(sub.id); }}
                                  className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-primary-600 hover:bg-primary-500 text-white font-bold rounded-xl transition-all shadow-lg shadow-primary-500/20 hover:-translate-y-0.5"
                                >
                                  <RefreshCw className="w-5 h-5" />
                                  إعادة تسليم التكليف
                                </button>
                              ) : (
                                <div className="bg-white dark:bg-zinc-800/50 border border-slate-200 dark:border-white/10 rounded-xl p-5 space-y-4">
                                  <div className="flex items-center justify-between">
                                    <h4 className="font-black text-slate-900 dark:text-white flex items-center gap-2">
                                      <Upload className="w-5 h-5 text-primary-500" />
                                      إعادة رفع التكليف
                                    </h4>
                                    <button onClick={resetResubForm} className="p-1.5 text-slate-400 hover:text-rose-500 bg-slate-100 dark:bg-white/5 rounded-lg transition-colors">
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>

                                  {/* Upload Type Tabs */}
                                  <div className="flex gap-2">
                                    {[{ key: 'images', label: 'صور', icon: ImageIcon }, { key: 'pdf', label: 'PDF', icon: FileText }, { key: 'drive', label: 'رابط', icon: LinkIcon }].map(t => (
                                      <button key={t.key} onClick={() => setResubTab(t.key)}
                                        className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-all border ${resubTab === t.key
                                          ? 'bg-primary-50 dark:bg-primary-500/10 border-primary-300 dark:border-primary-500/30 text-primary-700 dark:text-primary-400'
                                          : 'bg-slate-50 dark:bg-white/5 border-slate-200 dark:border-white/10 text-slate-500 dark:text-zinc-400 hover:border-slate-300'
                                          }`}
                                      >
                                        <t.icon className="w-4 h-4" />
                                        {t.label}
                                      </button>
                                    ))}
                                  </div>

                                  {/* Images Upload */}
                                  {resubTab === 'images' && (
                                    <div className="space-y-3">
                                      <input ref={imageInputRef} type="file" accept="image/*" multiple onChange={handleResubImageChange} className="hidden" />
                                      {resubImages.length > 0 && (
                                        <div className="flex gap-3 flex-wrap">
                                          {resubImages.map((img, i) => (
                                            <div key={i} className="relative group">
                                              <img src={img.preview} alt="" className="w-20 h-20 rounded-xl object-cover border-2 border-slate-200 dark:border-white/10" />
                                              <button onClick={() => setResubImages(prev => prev.filter((_, idx) => idx !== i))}
                                                className="absolute -top-2 -right-2 w-6 h-6 bg-rose-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md">
                                                <X className="w-3 h-3" />
                                              </button>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      {resubImages.length < 3 && (
                                        <button onClick={() => imageInputRef.current?.click()}
                                          className="w-full py-4 border-2 border-dashed border-slate-300 dark:border-white/10 rounded-xl text-slate-500 dark:text-zinc-400 hover:border-primary-400 hover:text-primary-600 transition-colors flex items-center justify-center gap-2 font-bold text-sm">
                                          <Upload className="w-5 h-5" />
                                          اختر صور ({resubImages.length}/3) — حد أقصى 2MB
                                        </button>
                                      )}
                                    </div>
                                  )}

                                  {/* PDF Upload */}
                                  {resubTab === 'pdf' && (
                                    <div className="space-y-3">
                                      <input ref={pdfInputRef} type="file" accept="application/pdf" onChange={handleResubPdfChange} className="hidden" />
                                      {resubPdf ? (
                                        <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-black/30 rounded-xl border border-slate-200 dark:border-white/10">
                                          <FileText className="w-8 h-8 text-rose-500 shrink-0" />
                                          <span className="font-bold text-sm text-slate-700 dark:text-zinc-200 flex-1 truncate" dir="ltr">{resubPdf.name}</span>
                                          <button onClick={() => setResubPdf(null)} className="text-slate-400 hover:text-rose-500"><X className="w-4 h-4" /></button>
                                        </div>
                                      ) : (
                                        <button onClick={() => pdfInputRef.current?.click()}
                                          className="w-full py-4 border-2 border-dashed border-slate-300 dark:border-white/10 rounded-xl text-slate-500 dark:text-zinc-400 hover:border-primary-400 hover:text-primary-600 transition-colors flex items-center justify-center gap-2 font-bold text-sm">
                                          <Upload className="w-5 h-5" />
                                          اختر ملف PDF — حد أقصى 10MB
                                        </button>
                                      )}
                                    </div>
                                  )}

                                  {/* Drive Link */}
                                  {resubTab === 'drive' && (
                                    <input
                                      type="url"
                                      placeholder="https://drive.google.com/..."
                                      value={resubDriveLink}
                                      onChange={e => setResubDriveLink(e.target.value)}
                                      className="w-full px-4 py-3 bg-slate-50 dark:bg-black/30 border border-slate-200 dark:border-white/10 rounded-xl text-slate-900 dark:text-white font-bold text-sm focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                                      dir="ltr"
                                    />
                                  )}

                                  {resubLoading && (
                                    <div className="space-y-1">
                                      <div className="h-2 bg-slate-100 dark:bg-white/5 rounded-full overflow-hidden">
                                        <div className="h-full bg-primary-500 rounded-full transition-all duration-500" style={{ width: `${resubProgress}%` }} />
                                      </div>
                                      <p className="text-xs font-bold text-slate-400 dark:text-zinc-500 text-center">{resubProgress}%</p>
                                    </div>
                                  )}

                                  {/* Resubmit Note Field */}
                                  <div className="space-y-2">
                                    <label className="flex items-center gap-2 text-xs font-black text-slate-500 dark:text-zinc-500">
                                      <MessageSquare className="w-3.5 h-3.5" /> ملاحظة جديدة للمدرب (اختيارية)
                                    </label>
                                    <textarea
                                      value={resubNote}
                                      onChange={(e) => setResubNote(e.target.value)}
                                      placeholder="أخبر المدرب بالتعديلات التي قمت بها..."
                                      className="w-full p-4 bg-slate-50 dark:bg-black/30 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-slate-900 dark:text-white transition-all font-medium resize-none text-sm min-h-[80px]"
                                      disabled={resubLoading}
                                    />
                                  </div>

                                  {/* Submit Button */}
                                  <button
                                    onClick={() => handleResubmit(sub)}
                                    disabled={resubLoading}
                                    className="w-full flex items-center justify-center gap-2 px-5 py-3.5 bg-primary-600 hover:bg-primary-500 disabled:bg-slate-300 disabled:dark:bg-zinc-700 text-white font-bold rounded-xl transition-all shadow-lg shadow-primary-500/20"
                                  >
                                    {resubLoading ? (
                                      <><Loader2 className="w-5 h-5 animate-spin" /> جاري الرفع...</>
                                    ) : (
                                      <><RefreshCw className="w-5 h-5" /> إرسال التعديل</>
                                    )}
                                  </button>
                                </div>
                              )}
                            </div>
                          )}

                          {/* ── Graded Feedback ── */}
                          {sub.status === 'graded' && sub.adminFeedback && (
                            <div className="bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 rounded-xl p-4 flex items-start gap-3">
                              <MessageSquare className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                              <div>
                                <p className="font-black text-emerald-700 dark:text-emerald-400 text-sm mb-1">تعليق المدرب</p>
                                <p className="text-emerald-600 dark:text-emerald-300 font-medium text-sm leading-relaxed">{sub.adminFeedback}</p>
                              </div>
                            </div>
                          )}

                          {/* ── Grade Card with Percentage ── */}
                          {sub.status === 'graded' && sub.grade != null && (
                            <div className="bg-gradient-to-l from-emerald-500/10 to-transparent rounded-xl p-5 flex items-center gap-5 border border-emerald-200 dark:border-emerald-500/20">
                              <div className="relative shrink-0">
                                <GradeRing percent={gradePercent} size={80} stroke={6} />
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                  <span className="text-xl font-black text-slate-900 dark:text-white leading-none">{gradePercent}%</span>
                                </div>
                              </div>
                              <div className="flex-1">
                                <p className="font-black text-slate-900 dark:text-white text-lg">
                                  {sub.grade} / {sub.maxScore || 100}
                                </p>
                                <p className="text-sm font-medium text-slate-500 dark:text-zinc-400 mb-2">الدرجة النهائية</p>
                                {(() => {
                                  const label = getGradeLabel(gradePercent);
                                  return (
                                    <span className={`px-3 py-1 rounded-full text-xs font-black ${label.cls}`}>
                                      {label.text}
                                    </span>
                                  );
                                })()}
                              </div>
                              <div className="hidden sm:flex flex-col items-center gap-1 shrink-0">
                                <TrendingUp className="w-5 h-5 text-emerald-500" />
                                <span className="text-[10px] font-bold text-slate-400 dark:text-zinc-500">تُضاف للدرجة الكلية</span>
                              </div>
                            </div>
                          )}

                          {/* ── Student Note ── */}
                          {sub.studentNote && (
                            <div className="bg-slate-50 dark:bg-black/20 p-4 rounded-xl border border-slate-200 dark:border-white/5 space-y-2">
                              <div className="flex items-center gap-2 text-xs font-black text-slate-500 dark:text-zinc-500 uppercase tracking-wider">
                                <MessageSquare className="w-3.5 h-3.5" /> ملاحظتك للمدرب
                              </div>
                              <p className="text-sm font-medium text-slate-700 dark:text-zinc-300 leading-relaxed italic">
                                "{sub.studentNote}"
                              </p>
                            </div>
                          )}

                          {/* ── Attached Files ── */}
                          {sub.files && sub.files.length > 0 && (
                            <div>
                              <h4 className="font-bold text-sm text-slate-600 dark:text-zinc-300 mb-3 flex items-center gap-2">
                                <Download className="w-4 h-4 text-slate-400" />
                                الملفات المرفقة ({sub.files.length})
                              </h4>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {sub.files.map((file, i) => (
                                  <a key={i} href={file.url} target="_blank" rel="noreferrer"
                                    className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-black/30 rounded-xl border border-slate-200 dark:border-white/10 hover:border-primary-400 transition-colors group">
                                    {file.url?.match(/\.(jpeg|jpg|gif|png|webp)$/i) ? (
                                      <img src={file.url} alt="att" className="w-10 h-10 rounded-lg object-cover border border-slate-200 dark:border-white/5" />
                                    ) : (
                                      <div className="w-10 h-10 rounded-lg bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center shrink-0">
                                        <FileText className="w-5 h-5 text-primary-500" />
                                      </div>
                                    )}
                                    <span className="font-bold text-sm text-slate-700 dark:text-zinc-200 truncate group-hover:text-primary-600 transition-colors flex-1" dir="ltr">
                                      {file.name || 'ملف مرفق'}
                                    </span>
                                    <ExternalLink className="w-4 h-4 text-slate-300 dark:text-zinc-600 group-hover:text-primary-500 transition-colors shrink-0" />
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* ── Drive Link ── */}
                          {sub.driveLink && (
                            <div>
                              <h4 className="font-bold text-sm text-slate-600 dark:text-zinc-300 mb-3 flex items-center gap-2">
                                <LinkIcon className="w-4 h-4 text-slate-400" />
                                رابط مشترك
                              </h4>
                              <a href={sub.driveLink} target="_blank" rel="noreferrer"
                                className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-500/10 rounded-xl border border-blue-200 dark:border-blue-500/20 hover:border-blue-400 transition-colors group">
                                <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center shrink-0">
                                  <LinkIcon className="w-5 h-5 text-blue-500" />
                                </div>
                                <span className="font-bold text-sm text-blue-700 dark:text-blue-300 truncate flex-1" dir="ltr">{sub.driveLink}</span>
                                <ExternalLink className="w-4 h-4 text-blue-300 group-hover:text-blue-500 transition-colors shrink-0" />
                              </a>
                            </div>
                          )}

                          {/* Grading Date */}
                          {sub.gradedAt && (
                            <p className="text-xs font-medium text-slate-400 dark:text-zinc-600 text-left" dir="ltr">
                              تاريخ التقييم: {sub.gradedAt?.toDate ? sub.gradedAt.toDate().toLocaleDateString('en-GB') : new Date(sub.gradedAt).toLocaleDateString('en-GB')}
                            </p>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}

      {/* ── Submission Modal ── */}
      <AnimatePresence>
        {submissionModalData && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSubmissionModalData(null)}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-2xl bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl overflow-hidden border border-white/10"
            >
              <div className="p-6 border-b border-slate-100 dark:border-white/5 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-black text-slate-900 dark:text-white">رفع التكليف</h3>
                  <p className="text-sm font-bold text-slate-500 dark:text-zinc-500">{submissionModalData.title}</p>
                </div>
                <button
                  onClick={() => setSubmissionModalData(null)}
                  className="p-2 hover:bg-slate-100 dark:hover:bg-white/5 rounded-xl transition-colors text-slate-400"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6 max-h-[80vh] overflow-y-auto custom-scrollbar">
                <AssignmentSubmission
                  sessionData={submissionModalData}
                  courseTitle={submissionModalData.courseTitle}
                  courseId={submissionModalData.courseId}
                  currentUser={currentUser}
                  onSuccess={() => {
                    setSubmissionModalData(null);
                    addToast('تم رفع التكليف بنجاح!', 'success');
                  }}
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
